import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../auth";
import { supabase } from "../supabaseClient";
import NotifyOptIn from "./NotifyOptIn";
import {
  notifyPrefsFromProfile,
  notifyPrefsToProfilePatch,
  useNotifyChannels,
} from "../notifySettings";
import { friendlyError } from "../utils/errors";

const ProfileEdit = () => {
  const { user, profile, refreshProfile } = useAuth();
  const { channels: notifyChannels } = useNotifyChannels();
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [notifyPhoneChannel, setNotifyPhoneChannel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  // True while the form has edits that aren't saved yet. The profile object is
  // re-fetched on every auth token refresh; syncing then would wipe the edits.
  const dirtyRef = useRef(false);
  const markDirty = () => {
    dirtyRef.current = true;
    setSaved(false);
  };

  useEffect(() => {
    if (dirtyRef.current) return;
    setFullName(profile?.full_name || "");
    setPhone(profile?.phone || "");
    const prefs = notifyPrefsFromProfile(profile, notifyChannels);
    setNotifyEmail(prefs.email);
    setNotifyPhoneChannel(prefs.phoneChannel);
  }, [profile, notifyChannels]);

  const onSave = async (e) => {
    e.preventDefault();
    setError(null);
    setSaved(false);

    if (!user) return;

    const name = fullName.trim();
    const phoneValue = phone.trim();
    if (!name) {
      setError("El nombre es obligatorio.");
      return;
    }
    if (!phoneValue || phoneValue.replace(/\D/g, "").length < 7) {
      setError("Ingresa un teléfono válido.");
      return;
    }

    setBusy(true);
    try {
      const notifyPatch = notifyPrefsToProfilePatch(
        { email: notifyEmail, phoneChannel: notifyPhoneChannel },
        notifyChannels
      );
      const { error: updateError } = await supabase
        .from("profiles")
        .update({
          full_name: name,
          phone: phoneValue,
          ...notifyPatch,
        })
        .eq("id", user.id);

      if (updateError) throw updateError;
      dirtyRef.current = false;
      await refreshProfile(user.id);
      setSaved(true);
    } catch (err) {
      console.error(err);
      setError(friendlyError(err, "No se pudo guardar. Inténtalo de nuevo."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="reg-form acct-form" onSubmit={onSave} noValidate>
      <label className="reg-field">
        <span className="reg-label">
          Nombre completo <span className="reg-req">*</span>
        </span>
        <input
          type="text"
          name="name"
          value={fullName}
          onChange={(e) => {
            setFullName(e.target.value);
            markDirty();
          }}
          autoComplete="name"
          disabled={busy}
          maxLength={80}
          required
        />
      </label>

      <label className="reg-field">
        <span className="reg-label">
          Teléfono <span className="reg-req">*</span>
        </span>
        <input
          type="tel"
          name="tel"
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value);
            markDirty();
          }}
          autoComplete="tel"
          inputMode="tel"
          disabled={busy}
          maxLength={30}
          required
        />
        <span className="reg-hint">
          La liga lo usa para contactarte sobre tus equipos y solicitudes.
        </span>
      </label>

      <NotifyOptIn
        channels={notifyChannels}
        email={notifyEmail}
        phoneChannel={notifyPhoneChannel}
        onEmailChange={(value) => {
          setNotifyEmail(value);
          markDirty();
        }}
        onPhoneChannelChange={(value) => {
          setNotifyPhoneChannel(value);
          markDirty();
        }}
        disabled={busy}
      />

      <div aria-live="polite">
        {error && (
          <div className="reg-status error" role="alert">
            {error}
          </div>
        )}
        {saved && (
          <div className="reg-success" role="status">
            Datos guardados.
          </div>
        )}
      </div>

      <button type="submit" className="reg-submit acct-save" disabled={busy}>
        {busy ? "Guardando…" : "Guardar perfil"}
      </button>
    </form>
  );
};

export default ProfileEdit;
